import { Link } from 'react-router-dom'

import { StatusDot, type Tone } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'
import { queueProgress } from '@/lib/deliverables'
import { daysUntil, formatDateTerse, formatDaysUntil } from '@/lib/dates'
import { formatMoneyWhole, townFrom } from '@/lib/format'
import { JOB_KIND_LABEL, TEAM_CONFIRM_DAYS, jobName } from '@/lib/jobs'
import { LabelBars } from '@/features/labels/Labels'
import type { BoardJob } from './queries'

/**
 * A card on the board.
 *
 * Shows what the brief asks for — couple, date, venue town, days until, and a
 * dot for where the money is — and nothing else. A board of forty weddings only
 * works if a card can be read at a glance, so anything that needs a second look
 * belongs on the job page instead.
 *
 * The couple's names are set in Bodoni italic. It is the one place on this
 * screen the display face appears, which is what makes it read as a name rather
 * than a row of data.
 */

/** Where the money stands, as one dot. */
export function depositTone(job: Pick<BoardJob, 'deposit_status' | 'agreed_total'>): { tone: Tone; label: string } {
  if (job.agreed_total === null) return { tone: 'neutral', label: 'No price agreed yet' }

  switch (job.deposit_status) {
    case 'paid':
      return { tone: 'sage', label: 'Deposit paid' }
    case 'invoiced':
      return { tone: 'amber', label: 'Deposit invoiced, not yet paid' }
    case 'waived':
      return { tone: 'neutral', label: 'Deposit waived' }
    case 'pending':
    default:
      return { tone: 'clay', label: 'Deposit not invoiced' }
  }
}

export function JobCard({ job, dragging = false }: { job: BoardJob; dragging?: boolean }) {
  const days = daysUntil(job.event_date)
  const deposit = depositTone(job)
  const town = job.venue?.town ?? townFrom(job.venue_name)
  const name = jobName(job)
  // Weddings and couple shoots promise the team 4–6 weeks before.
  const teamToConfirm =
    (job.kind === 'wedding' || job.kind === 'couple_shoot') &&
    job.deposit_status !== 'pending' &&
    !job.team_confirmed_at &&
    days >= 0 &&
    days <= TEAM_CONFIRM_DAYS

  // Who is on the day, as on her Trello cards; released seats are gone.
  const crew = (job.team ?? [])
    .filter((seat) => seat.status !== 'released' && seat.member)
    .map((seat) => seat.member!.name)
    .join(', ')
  // After the wedding: how far the editing is, in ticked checkpoints.
  const photoProgress = queueProgress(job.deliverables ?? [], 'photos')
  const filmProgress = queueProgress(job.deliverables ?? [], 'film')
  const photos = photoProgress.total ? photoProgress : null
  const film = filmProgress.total ? filmProgress : null

  return (
    <article
      className={cn(
        'card group relative w-full px-3 py-2.5 text-left transition-shadow duration-[var(--dur-fast)]',
        dragging ? 'shadow-[var(--shadow-menu)]' : 'hover:ring-2 hover:ring-accent',
      )}
    >
      <LabelBars labels={(job.labels ?? []).map((row) => row.label).filter((label) => label !== null)} className="mb-1.5" />
      <div className="flex items-start justify-between gap-2">
        <p className="display min-w-0 flex-1 truncate text-[0.9375rem] leading-tight">
          {name}
        </p>
        <StatusDot tone={deposit.tone} title={deposit.label} className="mt-[6px]" />
      </div>

      <div className="mt-1.5 flex items-baseline gap-2 text-[length:var(--text-2xs)]">
        <time dateTime={job.event_date} className="font-medium">
          {formatDateTerse(job.event_date)}
        </time>
        <span
          className={cn(
            'truncate',
            days < 0 ? 'text-taupe' : days <= 7 ? 'text-clay' : days <= 30 ? 'text-amber' : 'text-taupe',
          )}
        >
          {formatDaysUntil(job.event_date)}
        </span>
      </div>

      {job.kind !== 'wedding' || teamToConfirm ? (
        <p className="mt-0.5 flex gap-2 text-[length:var(--text-2xs)]">
          {job.kind !== 'wedding' ? <span className="label">{JOB_KIND_LABEL[job.kind]}</span> : null}
          {teamToConfirm ? <span className="text-amber">Confirm the team</span> : null}
        </p>
      ) : null}

      {town && town !== '—' ? <p className="mt-0.5 truncate text-[length:var(--text-2xs)] text-taupe">{town}</p> : null}

      {crew ? <p className="mt-0.5 truncate text-[length:var(--text-2xs)] text-taupe">{crew}</p> : null}

      {days < 0 && (photos || film) ? (
        <p className="mt-1 flex flex-wrap gap-x-3 text-[length:var(--text-2xs)]">
          {photos ? <span className={photos.done === photos.total ? 'text-sage' : ''}>Photos {photos.done}/{photos.total}</span> : null}
          {film ? <span className={film.done === film.total ? 'text-sage' : ''}>Film {film.done}/{film.total}</span> : null}
        </p>
      ) : null}

      {job.agreed_total !== null ? (
        <p className="mt-1 text-[length:var(--text-2xs)] text-taupe" data-numeric>
          {formatMoneyWhole(Number(job.agreed_total))}
        </p>
      ) : null}

      {/* The whole card is the link, stretched over it, so the drag handle and
          the navigation target can be the same rectangle without nesting a
          button inside an anchor. */}
      <Link
        to={`/weddings/${job.id}`}
        className="absolute inset-0 focus-visible:outline-2 focus-visible:-outline-offset-2"
        aria-label={`${name}, ${formatDateTerse(job.event_date)}`}
      />
    </article>
  )
}
